import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Box, DialogActions, DialogTitle, DialogContent } from '@mui/material';

import { Button, SearchForm, TableContainer, Typography } from '@occc/ui-design-system';
import { KioskAssetFilter } from '@occc/graphql';
import { PaginationInfo } from '@occc-app/hooks/usePaginatedQuery';

import { AssignAssetTable } from './assign-asset-table.component';

import { useGetKioskAssets } from '../../hooks/use-get-kiosk-assets.hook';
import { CANCEL_BUTTON_TEXT, SAVE_BUTTON_TEXT } from '../../touchpoint.constants';

type AssignAssetTableContainerProps = {
  onClose: () => void;
  onConfirm: (assetId: string) => void;
  initialSelectedAssetId?: string | null;
};

export const AssignAssetTableContainer = ({
  onClose,
  onConfirm,
  initialSelectedAssetId = null,
}: AssignAssetTableContainerProps) => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | undefined>();
  const previousInitialId = useRef<string | null>(null);
  const [localSelectedAssetId, setLocalSelectedAssetId] = useState<string | null>(
    initialSelectedAssetId || null,
  );

  const assetFilter = useMemo(() => {
    const filter: KioskAssetFilter = {
      searchTerm: search || undefined,
    };

    return filter;
  }, [search]);

  const { loading, error, data, paginationProps } = useGetKioskAssets({
    filter: assetFilter,
    paginationInfo,
    onPageChange: setPaginationInfo,
  });

  useEffect(() => {
    const initialIdString = initialSelectedAssetId || null;
    const previousIdString = previousInitialId.current;

    if (initialIdString !== previousIdString) {
      previousInitialId.current = initialSelectedAssetId || null;
      setLocalSelectedAssetId(initialSelectedAssetId || null);
    }
  }, [initialSelectedAssetId]);

  const onSubmit = async () => {
    if (localSelectedAssetId) {
      onConfirm(localSelectedAssetId);
      onClose();
    }
  };

  const handleSearchSubmit = useCallback(({ search }: { search?: string }) => {
    setSearch(search || undefined);
    setPaginationInfo(undefined);
  }, []);

  const headerActions = useMemo(() => {
    return <SearchForm onSubmit={handleSearchSubmit} />;
  }, [handleSearchSubmit]);

  return (
    <>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' whiteSpace='nowrap'>
            Select an asset
          </Typography>
          {headerActions}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <TableContainer>
          <AssignAssetTable
            loading={loading}
            data={data}
            error={error}
            paginationProps={paginationProps}
            selectedAssetId={localSelectedAssetId}
            onAssetSelect={setLocalSelectedAssetId}
          />
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button variant='text' onClick={onClose}>
          {CANCEL_BUTTON_TEXT}
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={onSubmit}
          disabled={!localSelectedAssetId}
        >
          {SAVE_BUTTON_TEXT}
        </Button>
      </DialogActions>
    </>
  );
};
